import { useState } from "react";
import { useAuthContext } from "../contexts/auth-context";
import { apiRequestGet, apiRequestPost, apiRequestSecuredDelete, apiRequestSecuredPatch, apiRequestSecuredPost } from "../lib/common/api";
import { TProcessStates, TGenericApiResponse } from "../lib/types/general";
import { TEmployeeRole } from "../lib/types/employee_roles";
import { TBusinessTypeQueryResults, TBusinessTypesFilters } from "../lib/types/business_types";
import { TSkills, TSkillsFilters, TSkillsResults } from "../lib/types/skills";

let industryRolesCache: TEmployeeRole[] = [];
const URLS = {
    getAllBusinessTypes: "business-types/all",
    getSkillListWithFilter: "industryroles/all/",
};
export const getIndustryRoles = async (): Promise<TEmployeeRole[]> => {
    if (industryRolesCache.length) return industryRolesCache;

    const industryRoles = await apiRequestGet<TGenericApiResponse<TEmployeeRole[]>>("employee-roles/just-names");

    industryRolesCache = industryRoles.data.data;
    return industryRoles.data.data;
};

export const getBusinessTypes = async (filterName: TBusinessTypesFilters) => {
    return (await apiRequestPost<TBusinessTypeQueryResults>(`${URLS.getAllBusinessTypes}`, filterName)).data.data;
};

export const getSkillListWithFilterNoLimit = async (page: number, tokenId: string, filterProps: TSkillsFilters, limit: number) => {
    const response = await apiRequestSecuredPost<TSkillsResults>(`${URLS.getSkillListWithFilter}${page}`, tokenId, {
        name: filterProps.name,
        limit: limit,
    });
    return response.data.data;
};

export const useIndustryRoles = () => {
    const { getUserTokenId } = useAuthContext();
    const [workingSkillState, setworkingSkillState] = useState<TProcessStates>("idle");
    const [positions, setPositions] = useState<TSkills[]>([]);

    const props: TSkillsFilters = { name: "" };
    const noLimitPageSize = 10000;

    const getSkillListNoLimit = async (offset: number, filterProps: TSkillsFilters) => {
        setworkingSkillState("working");
        const userTokenId = await getUserTokenId();
        const _skillList = await getSkillListWithFilterNoLimit(offset, userTokenId, filterProps, noLimitPageSize);
        setPositions(_skillList.skillsData);
        setworkingSkillState("done");
    };

    const resetSkillList = async () => {
        setworkingSkillState("working");
        await getSkillListNoLimit(0, props);
        setworkingSkillState("done");
    };

    const addIndustryRole = async (newPosition: string) => {
        const userTokenId = await getUserTokenId();
        return apiRequestSecuredPost("industryroles/add", userTokenId, {
            position: newPosition,
        });
    };

    const deleteIndustryRole = async (posId: string) => {
        const userTokenId = await getUserTokenId();
        const url = `industryroles/delete/${posId}`;

        return apiRequestSecuredDelete(url, userTokenId);
    };

    const updateIndustryRole = async (posId: string, newName: string) => {
        const userTokenId = await getUserTokenId();
        const url = `industryroles/update/${posId}`;
        return apiRequestSecuredPatch(url, userTokenId, { name: newName });
    };

    return {
        getSkillListNoLimit,
        addIndustryRole,
        deleteIndustryRole,
        updateIndustryRole,
        workingSkillState,
        positions,
        setPositions,
        resetSkillList,
    };
};
